import { useEffect, useMemo, useState } from 'react';
import {
  SlideOutPanelShell,
  slideOutPanelShellStyles as shellStyles,
  useSlideOutPanelChrome,
} from '@cowbell/ui';
import { JsonViewerContent } from '../../../../console/cmail/components/CmailDetailsResolver/CmailDetails/JsonViewerContent';
import styles from './JsonViewerPanel.module.css';

interface JsonViewerPanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly data: unknown;
  readonly chatWidth: number;
  readonly chatRight?: number;
}

/** Right-docked slide-out presentation of extracted-data JSON — the drawer
 *  counterpart to `JsonViewerModal`. Both render the same `JsonViewerContent`
 *  (toolbar + search + tree/raw viewer); this variant supplies the shared
 *  citation-panel chrome (backdrop, drag-resize, expand/collapse, focus trap,
 *  Esc) via `useSlideOutPanelChrome` + `SlideOutPanelShell`, same as
 *  `FilePreviewPanel`. Shell `.content` padding is overridden to 0 so the
 *  content's own toolbar spans the full width.
 *
 *  The host keeps this component mounted (like FilePreviewPanel) so close
 *  can pass `isOpen={false}` and the 300ms slide can run. The shell itself
 *  is deferred until the first open — otherwise FocusTrap from `@cowbell/ui`
 *  renders on every Ask Moo mount and breaks V2 wiring specs that only
 *  mock `@cowbell/ui/core`. */
export default function JsonViewerPanel({
  isOpen,
  onClose,
  data,
  chatWidth,
  chatRight = 0,
}: JsonViewerPanelProps) {
  const [hasOpened, setHasOpened] = useState(isOpen);
  const [heldData, setHeldData] = useState(data);
  // Bumped on each open / data change so JsonViewerContent remounts and
  // does not keep search, view mode, indent, or expand state from the last
  // document. The shell stays mounted for the close slide.
  const [viewerInstanceKey, setViewerInstanceKey] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setHasOpened(true);
    setHeldData(data);
    setViewerInstanceKey((previousKey) => previousKey + 1);
  }, [isOpen, data]);

  const chrome = useSlideOutPanelChrome({
    isOpen,
    hasSource: hasOpened,
    onClose,
    chatWidth,
    chatRight,
  });

  // Spread the shared shell classes and swap only `content` for the flush,
  // no-padding variant this panel needs (the JSON viewer manages its own
  // scroll region). A fresh object each render is fine — SlideOutPanelShell
  // only reads class names off it.
  const mergedStyles = useMemo(
    () => ({ ...shellStyles, content: styles.content }),
    []
  );

  if (!hasOpened) {
    return null;
  }

  return (
    <SlideOutPanelShell
      chrome={chrome}
      onClose={onClose}
      styles={mergedStyles}
      headerTitle="Extracted Data"
    >
      <JsonViewerContent key={viewerInstanceKey} data={heldData} />
    </SlideOutPanelShell>
  );
}
